// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"f7QyUxQ3AAg2LlwFbDOD5"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "/content-prod";import * as Sentry from '@sentry/nextjs';

const SENTRY_DSN =
  'https://f0b761b0d587f59396aaf3aaf3e34a2c@sentry-v2.clear.in//117';

interface SentryStackFrame {
  function?: string;
  lineno?: number;
  filename?: string;
  colno?: number;
}

const createErrorFingerprint = (event: Sentry.ErrorEvent): string[] => {
  if (event.exception?.values?.[0]) {
    const error = event.exception.values[0];
    return [
      error.type || 'Error',
      error.value || 'Unknown error',
      ...(error.stacktrace?.frames
        ?.slice(-3)
        .map(
          (frame: SentryStackFrame) =>
            `${frame.function || 'unknown'}:${frame.lineno || 0}`
        ) || []),
    ];
  }
  return [];
};

const createBeforeSendHandler =
  (isDevelopment = false) =>
  (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
    const fingerprint = createErrorFingerprint(event);
    if (fingerprint.length > 0) {
      event.fingerprint = fingerprint;
    }
    if (isDevelopment && event.type !== undefined && event.type !== 'error') {
      return null;
    }

    return event;
  };

// Common ignore patterns for all environments
const commonIgnoreErrors = [
  // GSI internal error codes
  /\boa\b/,
  // Generic GSI errors
  /google.*accounts.*id/i,
];

const commonDenyUrls = [
  // Google Sign-In client script
  /accounts\.google\.com\/gsi\/client/,
];

// Single function to initialize Sentry with environment-specific config
const initSentry = (env: string) => {
  const isLocal = env === 'local';
  const isProduction = env === 'production';

  Sentry.init({
    dsn: SENTRY_DSN,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: !isLocal, // Don't mask in local for easier debugging
        blockAllMedia: !isLocal,
      }),
    ],
    tracesSampleRate: isProduction ? 0.1 : 1.0, // Sample 10% in prod, 100% elsewhere
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    environment: env,
    debug: false,
    includeLocalVariables: !isProduction, // Don't include in production for security
    beforeSend: createBeforeSendHandler(isLocal),
    ignoreErrors: commonIgnoreErrors,
    denyUrls: commonDenyUrls,
  });
};

// Auto-initialize based on environment
const environment =
  process.env.NEXT_PUBLIC_APP_ENV || process.env.APP_ENV || 'local';

initSentry(environment);
